/**
 * useTransfers Hook
 *
 * React Query hooks for transfers.
 * @see Sprint 14 - Transferts Inter-Sites
 * @see US-14.5 - UI Liste et Creation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  transfersService,
  type QueryTransfersDto,
  type CreateTransferDto,
  type UpdateTransferDto,
  type ShipTransferDto,
  type ReceiveTransferDto,
  type CancelTransferDto,
} from '../services/transfers.service';

/**
 * Query key factory for transfers
 */
export const transferKeys = {
  all: ['transfers'] as const,
  lists: () => [...transferKeys.all, 'list'] as const,
  list: (params: QueryTransfersDto) => [...transferKeys.lists(), params] as const,
  details: () => [...transferKeys.all, 'detail'] as const,
  detail: (id: string) => [...transferKeys.details(), id] as const,
  kpis: () => [...transferKeys.all, 'kpis'] as const,
  sites: () => [...transferKeys.all, 'sites'] as const,
};

/**
 * Hook to fetch transfers list with pagination
 */
export function useTransfers(params: QueryTransfersDto) {
  return useQuery({
    queryKey: transferKeys.list(params),
    queryFn: () => transfersService.list(params),
    placeholderData: (prev) => prev,
  });
}

/**
 * Hook to fetch transfers KPIs
 */
export function useTransfersKPIs() {
  return useQuery({
    queryKey: transferKeys.kpis(),
    queryFn: () => transfersService.getKPIs(),
  });
}

/**
 * Hook to fetch available sites for transfer
 */
export function useTransferSites() {
  return useQuery({
    queryKey: transferKeys.sites(),
    queryFn: () => transfersService.getSites(),
  });
}

/**
 * Hook to fetch single transfer detail
 */
export function useTransfer(id: string | null) {
  return useQuery({
    queryKey: transferKeys.detail(id!),
    queryFn: () => transfersService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to create a transfer
 */
export function useCreateTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTransferDto) => transfersService.create(data),
    onSuccess: async (transfer) => {
      await queryClient.invalidateQueries({ queryKey: transferKeys.all, refetchType: 'active' });
      toast.success(`Demande ${transfer.transferNumber} creee`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la creation');
    },
  });
}

/**
 * Hook to update a transfer
 */
export function useUpdateTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTransferDto }) =>
      transfersService.update(id, data),
    onSuccess: async (transfer) => {
      await queryClient.invalidateQueries({ queryKey: transferKeys.all, refetchType: 'active' });
      toast.success(`Transfert ${transfer.transferNumber} mis a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour');
    },
  });
}

/**
 * Hook to delete a draft transfer
 */
export function useDeleteTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => transfersService.delete(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: transferKeys.all, refetchType: 'active' });
      toast.success('Demande supprimee');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la suppression');
    },
  });
}

/**
 * Hook to submit a draft transfer
 */
export function useSubmitTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => transfersService.submit(id),
    onSuccess: async (transfer) => {
      await queryClient.invalidateQueries({ queryKey: transferKeys.all, refetchType: 'active' });
      toast.success(`Demande ${transfer.transferNumber} soumise`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la soumission');
    },
  });
}

/**
 * Hook to ship a transfer
 */
export function useShipTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ShipTransferDto }) =>
      transfersService.ship(id, data),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: transferKeys.all, refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['stock-movements'], refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['current-stock'], refetchType: 'active' }),
      ]);
      toast.success(
        `Transfert ${result.transfer.transferNumber} expedie (${result.movementsCreated} mouvements)`
      );
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de l\'expedition');
    },
  });
}

/**
 * Hook to receive a transfer
 */
export function useReceiveTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReceiveTransferDto }) =>
      transfersService.receive(id, data),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: transferKeys.all, refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['stock-movements'], refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['current-stock'], refetchType: 'active' }),
      ]);
      if (result.hasDispute) {
        toast.error(`Transfert ${result.transfer.transferNumber} recu avec ecarts`);
      } else {
        toast.success(`Transfert ${result.transfer.transferNumber} receptionne`);
      }
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de la reception');
    },
  });
}

/**
 * Hook to cancel a transfer
 */
export function useCancelTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CancelTransferDto }) =>
      transfersService.cancel(id, data),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: transferKeys.all, refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['stock-movements'], refetchType: 'active' }),
        queryClient.invalidateQueries({ queryKey: ['current-stock'], refetchType: 'active' }),
      ]);
      toast.success(`Transfert ${result.transfer.transferNumber} annule`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      toast.error(error.response?.data?.error?.message ?? 'Erreur lors de l\'annulation');
    },
  });
}
